import React, { useEffect, useState } from 'react';
import RequestFormLayout from 'components/layout/RequestFormLayout';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import FormDetails from 'components/Form/FormDetails';
import { Button, Modal, notification, Spin } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import './ReviewForm.scss';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from 'core/types';
import { updateVisitRequestForm, amendActionRequest, cancelAmendmentRequest } from 'core/visitRequestForm/visitRequestFormApi';
import useRequestFlow from 'core/guestBooking/useRequestFlow';
import Checkbox from 'components/Elements/Checkbox';
import { unwrapResult } from '@reduxjs/toolkit';
import { onBehalfEmailError } from 'core/utils/onBehalfEmailError';
import { VisitRequestFormResponsePayload } from 'core/visitRequestForm/types';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';
import TermsAndConditions from '../components/TermsAndConditionsModal';

const ReviewForm = () => {
    const navigation = useNavigate();
    const { state: navState } = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const flow = useRequestFlow();
    const { visitRequest, isUpdating, isAmending } = useSelector((state: CombinedStates) => state.visitRequestForm);
    const { isFetching } = useSelector((state: CombinedStates) => state.visitRequestReview);
    const authedUserData = useSelector((state: CombinedStates) => state.user.userData);
    const authedUserEmail = authedUserData?.email;
    // Never read authenticated user state in a guest session — persisted
    // userData from another tab must not influence the guest declaration.
    const currentUserEmail = flow.guestMode ? undefined : authedUserEmail;
    const [termModalOpen, setTermModalOpen] = useState(false);
    const [isTermAccepted, setIsTermAccepted] = useState(false);
    const [amendModalOpen, setAmendModalOpen] = useState(false);
    const [signedBy, setSignedBy] = useState('');
    const [userEmail, setUserEmail] = useState('');

    const [amendedSections, setAmendedSections] = useState<string[]>([]);
    const [additionalDetails, setAdditionalDetails] = useState<string>('');
    const amending = navState?.amending;

    // Auto-cleanup request if user leaves without completing
    useFormCleanup({
        requestId: (!amending && !flow.guestMode) ? visitRequest.encryptedId : null,
        formBasePath: '/visitor-request/',
        deleteEndpoint: 'visit-request',
    });

    const handleSubmit = () => {
        if (!amending) {
            flow.submitReview({
                agreed_to_terms: isTermAccepted,
                signature: signedBy,
                email: userEmail,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (flow.guestMode) {
                    navigation('/booking/complete', { state: null });
                } else if (payload.stepData) {
                    navigation('/dashboard', { state: null });
                }
            }).catch(reportUnexpectedRejection);
        } else {
            let error = { message: '', description: '' };
            if (amendedSections.length === 0) {
                error = {
                    message: 'No Amendments Made',
                    description: 'Please select at least one section to amend',
                };
            }

            if (error.message !== '' && error.description !== '') {
                notification.error(error);
                return;
            }

            const sections = amendedSections.map((section) => section);

            dispatch(updateVisitRequestForm({
                id: String(id),
                step: 6,
                data: {
                    declaration: {
                        agreed_to_terms: isTermAccepted,
                        signature: signedBy,
                        email: userEmail,
                    },
                    amending,
                    forcedStep: 6,
                },
            })).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    dispatch(amendActionRequest({
                        id: String(id),
                        sections,
                        details: additionalDetails,
                    })).then(unwrapResult).then((amendPayload: { message: string, success: boolean }) => {
                        if (amendPayload.success) {
                            navigation(`/visitor-view-request/${id}`, { state: { fromForm: true } });
                        }
                    }).catch(reportUnexpectedRejection);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleNextClick = () => {
        let error = { message: '', description: '' };
        if (!isTermAccepted) {
            error = {
                message: 'Terms & Conditions not accepted',
                description: 'Please accept the terms & conditions to proceed.',
            };
        }

        // Malformed, or the submitter's own address. Shared with the access and
        // guarding forms so all three reject the same things.
        const emailError = onBehalfEmailError(userEmail, currentUserEmail);
        if (emailError) {
            error = emailError;
        }

        if (error.message !== '' && error.description !== '') {
            notification.error(error);
            return;
        }

        if (amending) {
            setAmendModalOpen(true);
        } else {
            handleSubmit();
        }
    };

    const handleAbortChanges = () => {
        if (id !== undefined) {
            dispatch(cancelAmendmentRequest(id)).then(() => {
                navigation(`/visitor-view-request/${id}`, { state: { fromForm: true } });
            });
        }
    };

    const handleInitialLoad = () => {
        if (id !== undefined || flow.guestMode) {
            flow.loadStep(6, amending);
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Visit Request',
                description: 'Please select a visit request to proceed',
            });
        }
    };

    useEffect(() => {
        handleInitialLoad();
    }, []);

    useEffect(() => {
        if (visitRequest.requester?.name) {
            setSignedBy(visitRequest.requester.name);
        } else if (flow.guestMode && visitRequest.guestRequesterName) {
            setSignedBy(visitRequest.guestRequesterName);
        } else if (visitRequest.declaration?.signature) {
            // Fallback (e.g. a resubmitted draft): the previously-captured
            // signature is copied onto the new request, so use it when the
            // requester relation isn't present on the payload.
            setSignedBy(visitRequest.declaration.signature);
        } else if (!flow.guestMode && authedUserData?.name) {
            // Last resort for the authenticated flow: the person completing the
            // request is the logged-in user, so sign with their own name. This
            // guarantees the Signed field (and the submitted signature) is
            // populated even before the request payload has fully loaded.
            setSignedBy(authedUserData.name);
        }
        // Prefill the "on behalf of" email when amending. Never for a guest —
        // the guest IS the requester, not delegating, so it must stay empty.
        if (amending && visitRequest.onBehalfEmail) {
            setUserEmail(visitRequest.onBehalfEmail);
        }
    }, [visitRequest, amending, flow.guestMode, authedUserData]);

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        flow.goToStep(5, navigationState);
    };

    const handleRemoveDate = (dateId: number) => {
        if (id !== undefined) {
            dispatch(updateVisitRequestForm({
                id,
                step: 2,
                data: {
                    selectedDates: visitRequest.dates?.filter((date) => date.id !== dateId),
                    amending,
                    forcedStep: 2,
                },
            })).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    handleInitialLoad();
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleCheckboxChange = (section: string) => {
        if (amendedSections.includes(section)) {
            setAmendedSections((prev) => prev.filter((s) => s !== section));
        } else {
            setAmendedSections((prev) => [...prev, section]);
        }
    };

    return (
        <div>
            <RequestFormLayout
                currentStep={6}
                isNextEnabled={isTermAccepted}
                isBackEnabled
                onBackClick={handleBackClick}
                nextText="Submit"
                onNextClick={handleNextClick}>
                <Spin spinning={isUpdating || isFetching}>
                    <FormDetails
                        onPressTerm={() => setTermModalOpen(true)}
                        details={visitRequest}
                        id={id}
                        signedBy={signedBy}
                        onAcceptTerm={(accept) => setIsTermAccepted(accept)}
                        onRemoveDate={(dateId) => handleRemoveDate(dateId)}
                        amending={amending}
                        onEmailChange={(email) => setUserEmail(email)}
                        userEmail={userEmail} />
                </Spin>
            </RequestFormLayout>
            <Modal title="Terms & Conditions For Access"
                wrapClassName="modal-container"
                onCancel={() => setTermModalOpen(false)}
                footer={null}
                styles={{ body: { maxHeight: '50vh', overflow: 'auto' } }}
                style={{ left: -200 }}
                open={termModalOpen}>
                <TermsAndConditions />
            </Modal>
            <Modal title="Amendments to Request"
                wrapClassName="amend-modal-container"
                onCancel={() => setAmendModalOpen(false)}
                footer={null}
                styles={{ body: { overflow: 'auto' } }}
                open={amendModalOpen}>
                <div>
                    <p>
                        Please select all the sections of your request that you made amendments to.
                        Depending on changes made, the request will return to the pending approval
                        status until an approver reviews your amendments.
                    </p>
                    <p className="amend-important-notice">
                        IMPORTANT - If making changes to Date or Shift please ensure the check box is selected so any resource allocated for granting access can be notified.
                    </p>
                    <div className="amend-checkbox-wrapper">
                        <div className="checkbox-wrapper">
                            <Checkbox checked={amendedSections.includes('Date or Shift')} onClick={() => handleCheckboxChange('Date or Shift')} />
                            <span className="checkbox-label">Date or Shift</span>
                        </div>
                        <div className="checkbox-wrapper">
                            <Checkbox checked={amendedSections.includes('Visitors')} onClick={() => handleCheckboxChange('Visitors')} />
                            <span className="checkbox-label">Visitors</span>
                        </div>
                        <div className="checkbox-wrapper">
                            <Checkbox checked={amendedSections.includes('Site Access')} onClick={() => handleCheckboxChange('Site Access')} />
                            <span className="checkbox-label">Site Access</span>
                        </div>
                        <div className="checkbox-wrapper">
                            <Checkbox checked={amendedSections.includes('Work Type')} onClick={() => handleCheckboxChange('Work Type')} />
                            <span className="checkbox-label">Work Type</span>
                        </div>
                    </div>
                    <div className="additional-details">
                        <h5 className="heading margin-5">Additional Details (Optional)</h5>
                        <textarea
                            className="details-textarea"
                            value={additionalDetails}
                            maxLength={MAX_LONG_TEXT_LENGTH}
                            placeholder=""
                            onChange={(e) => setAdditionalDetails(e.target.value)} />
                    </div>
                    <div className="button-wrapper">
                        <Button type="primary" className="form-button" onClick={handleSubmit} loading={isAmending || isUpdating}>
                            Save Amendments
                        </Button>
                        <Button type="primary" ghost className="form-button cancel-button" onClick={handleAbortChanges}>
                            Cancel Amendments
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>

    );
};

export default ReviewForm;
